import { Archive, BarChart3, Copy, Edit, MoreHorizontal, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSalonSlug } from "@/hooks/use-salon-slug";
import { EmptyState } from "@/lib/components/empty-state";
import { ErrorAlert } from "@/lib/components/error-alert";
import { useSalonStore } from "@/lib/store/salon";
import { useTrafficSources } from "../hooks/use-traffic-sources";
import { StorefrontUrlCopyDialog } from "./storefront-url-copy-dialog";
import { TrafficSourceDialog } from "./traffic-source-dialog";

export function TrafficSourcesConfig() {
  const { t } = useTranslation("settings");
  const currentSalon = useSalonStore((state) => state.currentSalon);
  const { slug } = useSalonSlug(currentSalon?.id);
  const { trafficSources, loading, error, isArchiving, archiveTrafficSource } = useTrafficSources();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTrafficSource, setEditingTrafficSource] = useState<any>(null);
  const [urlCopyDialogOpen, setUrlCopyDialogOpen] = useState(false);
  const [selectedTrafficSourceForUrl, setSelectedTrafficSourceForUrl] = useState<any>(null);

  const handleEdit = (trafficSource: any) => {
    setEditingTrafficSource(trafficSource);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingTrafficSource(null);
    setDialogOpen(true);
  };

  const handleArchive = async (id: string, archive: boolean) => {
    try {
      await archiveTrafficSource(id, archive);
    } catch (_error) {
      // Error is already handled in hook
    }
  };

  const handleCopyUrl = (trafficSource: any) => {
    if (!slug) {
      toast.error("Salon slug is required", {
        description:
          "Please set up the salon slug in the configuration before generating booking URLs.",
      });
      return;
    }

    setSelectedTrafficSourceForUrl(trafficSource);
    setUrlCopyDialogOpen(true);
  };

  const activeTrafficSources = trafficSources.filter((ts) => !ts.isArchived);
  const archivedTrafficSources = trafficSources.filter((ts) => ts.isArchived);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">{t("trafficSources.title")}</h3>
            <p className="text-sm text-muted-foreground">{t("trafficSources.description")}</p>
          </div>
          <Button onClick={handleCreate} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            {t("trafficSources.addTrafficSource")}
          </Button>
        </div>

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : trafficSources.length === 0 ? (
          <EmptyState
            icon={BarChart3}
            title={t("trafficSources.emptyState.active.title")}
            description={t("trafficSources.emptyState.active.description")}
          />
        ) : (
          <Tabs defaultValue="active" className="w-full">
            <TabsList className="grid grid-cols-2 w-fit">
              <TabsTrigger value="active" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                {t("trafficSources.sections.active")}
              </TabsTrigger>
              <TabsTrigger value="archived" className="flex items-center gap-2">
                <Archive className="h-4 w-4" />
                {t("trafficSources.sections.archived")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("trafficSources.sections.activeDescription")}
              </p>
              {activeTrafficSources.length === 0 ? (
                <EmptyState
                  icon={BarChart3}
                  title={t("trafficSources.emptyState.active.title")}
                  description={t("trafficSources.emptyState.active.description")}
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("trafficSources.name")}</TableHead>
                      <TableHead>{t("trafficSources.code")}</TableHead>
                      <TableHead>{t("trafficSources.description")}</TableHead>
                      <TableHead className="w-[80px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeTrafficSources.map((trafficSource) => (
                      <TableRow key={trafficSource.id}>
                        <TableCell className="font-medium">{trafficSource.name}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {trafficSource.code}
                          </code>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {trafficSource.description || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleCopyUrl(trafficSource)}
                              title={t("trafficSources.urlCopy.title")}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>
                                  {t("trafficSources.actionsLabel")}
                                </DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => handleEdit(trafficSource)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  {t("trafficSources.actions.edit")}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleArchive(trafficSource.id, true)}
                                  disabled={isArchiving}
                                >
                                  <Archive className="mr-2 h-4 w-4" />
                                  {t("trafficSources.actions.archive")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            <TabsContent value="archived" className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("trafficSources.sections.archivedDescription")}
              </p>
              {archivedTrafficSources.length === 0 ? (
                <EmptyState
                  icon={Archive}
                  title={t("trafficSources.emptyState.archived.title")}
                  description={t("trafficSources.emptyState.archived.description")}
                />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("trafficSources.name")}</TableHead>
                      <TableHead>{t("trafficSources.code")}</TableHead>
                      <TableHead>{t("trafficSources.description")}</TableHead>
                      <TableHead className="w-[80px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {archivedTrafficSources.map((trafficSource) => (
                      <TableRow key={trafficSource.id} className="opacity-75">
                        <TableCell className="font-medium">{trafficSource.name}</TableCell>
                        <TableCell>
                          <code className="px-2 py-1 bg-muted rounded text-sm">
                            {trafficSource.code}
                          </code>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {trafficSource.description || "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleCopyUrl(trafficSource)}
                              title={t("trafficSources.urlCopy.title")}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>{t("trafficSources.actions")}</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleArchive(trafficSource.id, false)}
                                  disabled={isArchiving}
                                >
                                  <BarChart3 className="mr-2 h-4 w-4" />
                                  {t("trafficSources.actions.restore")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <TrafficSourceDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        trafficSource={editingTrafficSource}
      />

      {/* URL Copy Dialog */}
      <StorefrontUrlCopyDialog
        open={urlCopyDialogOpen}
        onOpenChange={setUrlCopyDialogOpen}
        trafficSourceCode={selectedTrafficSourceForUrl?.code}
        trafficSourceName={selectedTrafficSourceForUrl?.name}
      />
    </>
  );
}
